import React, { useState, useEffect, useCallback } from "react";
import apiClientAdmin from "../../../services/apiClientAdmin";
import AssetForm from "../Asset-Form/AssetForm";
import AssetTable from "../Asset-Table/AssetTable";
import ErrorDialog from "../ErrorDialog/ErrorDialog";
import Dialog from "../FormDialog/Dialog";
import { getHeaders as getAssetHeaders } from "../../ApiHeaders/apiHeaders";
import { getHeaders as getEntitlementsHeaders } from "../../ApiHeaders/apiHeadersEntitlements";
import { datadogRum } from "@datadog/browser-rum";
import LoadingStyle from "../../Loading/Loading";
import { SplitDivider, useResizableSplit } from "../../SplitPane/ResizableSplit";

interface AssetSetupProps {
  registerTabSwitchGuard?: (guard: () => boolean | Promise<boolean>) => void;
  canDelete?: boolean;
}


const AssetSetup: React.FC<AssetSetupProps> = ({ 
  registerTabSwitchGuard,
  canDelete = false
}) => {
  const [assets, setAssets] = useState<any[]>([]);
  const [isoData] = useState<any[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<any | null>(null);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [error, setError] = useState<{ title: string; details: string } | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);

  const [dialog, setDialog] = useState<{
    open: boolean;
    title: string;
    message: React.ReactNode;
    confirmButtonText: string;
    cancelButtonText?: string;
    onConfirm: () => void;
    onCancel?: () => void;
  }>({
    open: false,
    title: "",
    message: "",
    confirmButtonText: "",
    onConfirm: () => {},
  });

  const closeDialog = () => {
    setDialog({
      open: false,
      title: "",
      message: "",
      confirmButtonText: "",
      onConfirm: () => {},
    });
  };
  useEffect(() => {
    if (registerTabSwitchGuard) {
      registerTabSwitchGuard(() => {
        return new Promise<boolean>((resolve) => {
          if (isFormDirty || isCreateMode) {
            setDialog({
              open: true,
              title: "Unsaved Changes",
              message:
                "You have unsaved changes. Are you sure you want to discard them?",
              confirmButtonText: "Yes",
              cancelButtonText: "No",
              onConfirm: () => {
                setIsFormDirty(false);
                setIsCreateMode(false);
                closeDialog();
                resolve(true);
              },
              onCancel: () => {
                closeDialog();
                resolve(false);
              },
            });
          } else {
            resolve(true);
          }
        });
      });
    }
  }, [isFormDirty, isCreateMode]);

  // const handleApiError = (err: any, fallbackTitle: string) => {
  //   let title = fallbackTitle;
  //   let details = "An unknown error occurred";

  //   if (err.response?.data?.error) {
  //     title = err.response.data.error.title || fallbackTitle;
  //     details = err.response.data.error.error.message || details;
  //   } else if (err.message) {
  //     details = err.message;
  //   }

  //   setError({ title, details });
  //   datadogRum.addError(err, { title, details });
  // };

  const handleApiError = (err: any, fallbackTitle: string) => {
    let title = fallbackTitle;
    let details = "An unknown error occurred";

    const statusCode = err.response?.status;
    const errorResponse = err.response?.data?.error;

    const backendMessage =
      errorResponse?.error?.message || errorResponse?.message;

    switch (statusCode) {
      case 400:
        title = "Bad Request";
        details = backendMessage || "The data you submitted is invalid.";
        break;
      case 401:
        title = "Acces Denied";
        details =
          "You're not authorized to access this content. Please log in or check your access permissions.";
        break;
      case 403:
        title = "Access Denied";
        details = "You need permission to access this site.";
        break;
      case 404:
        title = "Not Found";
        details =
          backendMessage || "The requested resource could not be found.";
        break;
      case 500:
        title = "Internal Server Error";
        details =
          backendMessage ||
          "Server error while fetching data. Please try again later.";
        break;
      default:
        if (
          err.message?.includes("Network Error") ||
          err.message?.includes("timeout")
        ) {
          title = "Network Error";
          details =
            "Unable to connect to the server. Check your internet connection.";
        } else {
          title = fallbackTitle;
          details =
            backendMessage || err.message || "An unexpected error occurred.";
        }
        break;
    }

    setError({ title, details });
    datadogRum.addError(err, { title, details });
  };

  const fetchAssets = useCallback(async () => {
    try {
      let allAssets: any[] = [];
      let page = 1;
      const pageSize = 200;
      let hasMore = true;

      const headers = await getAssetHeaders ();
      setLoading(true);

      while (hasMore) {
        const response = await apiClientAdmin.get(
          `/assets/?page=${page}&page_size=${pageSize}`,
          { headers }
        );
        const fetchedAssets = response.data.response.data;

        allAssets = [...allAssets, ...fetchedAssets];
        hasMore = fetchedAssets.length === pageSize;
        page += 1;
      }

      setAssets(allAssets);
      setLoading(false);
      datadogRum.addAction("Fetched all assets on mount", {
        count: allAssets.length,
      });
    } catch (err) {
      setLoading(false);
      handleApiError(err, "Failed to fetch assets");
    }
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const updateAssetGroupMapping = async (asset: any) => {
    const headers = await getEntitlementsHeaders();

    const payload = {
      asset_group_id: 61,
      asset_group_name: "BETM-ALL-Assets",
      description: "",
      company_name: "BETM",
      enabled: "Y",
      sys_created_by: asset.sys_created_by || "system",
      sys_modified_by: asset.sys_modified_by || "system",
      asset_names: [asset.asset_name],
    };

    try {
      await apiClientAdmin.post(`${process.env.REACT_APP_ENTITLEMENTS_API}/asset-group-mappings/`, payload, { headers });
      datadogRum.addAction("Asset mapped to group", { asset: asset.asset_name, asset_group: payload.asset_group_name });
    } catch (err) {
      console.error("Failed to update asset group mapping", err);
      datadogRum.addError(err, { title: "Failed to Update Asset Group Mapping" });
    }
  };

  const handleAddAsset = async (newAsset: any) => {
    const assetWithAuditFields = { ...newAsset };
    const headers = await getAssetHeaders();

    try {
      //  Create asset
      const createdAssetResponse = await apiClientAdmin.post("/assets/", assetWithAuditFields, { headers });

      //Refresh asset list
      await fetchAssets();

      // Extract created asset
      const createdAsset = createdAssetResponse.data?.response?.data[0] || assetWithAuditFields;
      const assetName = createdAsset.asset_name || newAsset.asset_name;

      //Show success dialog
      datadogRum.addAction("Asset added", { asset: assetName });
      setDialog({
        open: true,
        title: "Success",
        message: `Asset "${assetName}" successfully created.`,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });

      // Update asset group mapping ONLY if asset creation succeeded
      try {
        await updateAssetGroupMapping(createdAsset);
      } catch (mappingErr) {
        console.error("Asset group mapping failed", mappingErr);
        datadogRum.addError(mappingErr, { title: "Asset group mapping failed", asset: assetName });
      }

    } catch (err) {
      handleApiError(err, "Failed to Create Asset");
    }
  };

  
  const handleEditAsset = async (updatedAsset: any) => {
    const headers = await getAssetHeaders ();
    if (selectedAsset) {
      try {
        const assetToUpdate = { ...updatedAsset };
        await apiClientAdmin.put(
          `/assets/${selectedAsset.asset_id}`,
          assetToUpdate,
          { headers }
        );
        fetchAssets();

        setDialog({
          open: true,
          title: "Success",
          message: `Asset "${updatedAsset.asset_name}" successfully updated.`,
          confirmButtonText: "Close",
          onConfirm: () => {
            closeDialog();
            setSelectedAsset(null);
            setEditIndex(null);
          },
        });
        datadogRum.addAction("Asset updated", {
          asset: updatedAsset.asset_name,
        });
      } catch (err) {
        handleApiError(err, "Failed to Update Asset");
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedAsset) {
      try {
        await apiClientAdmin.delete(`/assets/${selectedAsset.asset_id}`);
        setSelectedAsset(null);
        fetchAssets();
        setDialog({
          open: true,
          title: "Success",
          message: `Asset "${selectedAsset.asset_name}" successfully deleted.`,
          confirmButtonText: "Close",
          onConfirm: closeDialog,
        });
      } catch (error) {
        handleApiError(error, "Failed to delete asset");
      }
    } else {
      setDialog({
        open: true,
        title: "No Selection",
        message: "Please select an asset to delete.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
    }
  };

  // const handleSelectRow = (selectedAsset: any) => {
  //   setSelectedAsset(selectedAsset);
  // };

  const handleRowSelect = (asset: any) => {
    if (isFormDirty || isCreateMode) {
      setDialog({
        open: true,
        title: "Unsaved Changes",
        message:
          "You have unsaved changes. Are you sure you want to discard them?",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          setIsFormDirty(false);
          setIsCreateMode(false);
          setSelectedAsset(asset);
          setSelectedRow(asset.asset_name);
          closeDialog();
        },
        onCancel: closeDialog,
      });
    } else {
      setSelectedAsset(asset);
      setSelectedRow(asset.asset_name);
    }
  };
  const onClearSelection = () => {
    if (isFormDirty) {
      setDialog({
        open: true,
        title: "Unsaved Changes",
        message:
          "You have unsaved changes. Are you sure you want to discard them?",
        // confirmButtonText: "Discard Changes",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          setIsFormDirty(false);
          setSelectedRow(null);
          setSelectedRow(null);
          closeDialog();
        },
        onCancel: closeDialog,
      });
    } else {
      setSelectedAsset(null);
      setSelectedRow(null);
    }
  };
  const split = useResizableSplit("xl");
  return (
    <div ref={split.containerRef} style={split.containerStyle} className={`flex flex-col xl:flex-row xl:items-stretch bg-gray-100 p-4 min-h-full gap-4 ${split.containerClassName}`}>
      {/* <div className="flex-grow flex flex-col lg:basis-1/3 xl:self-stretch bg-white relative"> */}
      <div className={`admin-tab-panel flex-grow flex flex-col lg:basis-1/3 xl:self-stretch bg-white relative ${split.leftClassName}`}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-30">
            <LoadingStyle />
          </div>
        )}
        <AssetTable
          assets={assets}
          selectedAsset={selectedAsset}
          setSelectedAsset={setSelectedAsset}
          onDeleteSelected={handleDeleteSelected}
          // fetchAssets={fetchAssets}
          onClearSelection={onClearSelection}
          onRowSelect={handleRowSelect}
          selectedRow={selectedRow}
        />
      </div>

      {/* <div className="flex-grow  lg:basis-1/3 flex flex-col h-full xl:flex-row xl:items-stretch xl:h-auto"> */}
      <SplitDivider {...split.dividerProps} />
      <div className="admin-tab-panel admin-tab-form-panel flex-grow flex flex-col lg:basis-1/4 xl:self-stretch bg-white rounded-xl shadow-md">
        
        <AssetForm
          onSaveAsset={handleAddAsset}
          onSaveChanges={handleEditAsset}
          editIndex={editIndex}
          asset={selectedAsset}
          isoData={isoData}
          selectedAsset={selectedAsset}
          setSelectedAsset={setSelectedAsset}
          handleApiError={handleApiError}
          onClearSelection={onClearSelection}
          onDirtyChange={setIsFormDirty}
          onModeChange={(mode) => setIsCreateMode(mode === 'create')}
          onDeleteAsset={handleDeleteSelected}
          canDelete={canDelete}

        />
      </div>

      {dialog.open && (
        <Dialog
          title={dialog.title}
          message={dialog.message}
          confirmButtonText={dialog.confirmButtonText}
          cancelButtonText={dialog.cancelButtonText}
          onCancel={dialog.onCancel}
          onConfirm={dialog.onConfirm}
          onClose={closeDialog}
        />
      )}
      {error && (
        <ErrorDialog
          title={error.title}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}
    </div>
  );
};

export default AssetSetup;
